import { computed, Signal } from '@angular/core';
import {
  applyToCart,
  calculateUpsellHints,
  PromotionCart,
  ApiV1_Promotion,
  PromotionResult,
  EvaluationContext,
  PromotionInput,
  UpsellHint,
  evaluateConditions,
  getPromotionsForProduct,
  toPromotionInput,
  appliedPromotionToInput,
} from '@pedix-workspace/shared-promotions';
import { expandCartItemsToPromotionCart, getCartItemSavings } from '@pedix-workspace/orders';
import { CartItem, getDateReferenceForTimezone } from '@pedix-workspace/utils';
import { StockMap } from '@pedix-workspace/shared-stock';

export interface CartPromotionsEngineProduct {
  id: string;
  name: string;
}

export interface CartPromotionsEngineInputs {
  cartItems: Signal<CartItem[]>;
  promotions: Signal<ApiV1_Promotion[]>;
  products: Signal<CartPromotionsEngineProduct[]>;
  timezone?: Signal<string | undefined>;
  /**
   * Optional stock signal. When provided, upsell hints are filtered to hide
   * those whose `neededQuantity` exceeds what the user can still add to the
   * cart given current stock (`availableStock - totalStockInCart`). When not
   * provided, all hints are returned unfiltered (backwards-compatible).
   */
  stock?: Signal<StockMap | null | undefined>;
}

export class CartPromotionsEngine {
  private readonly promotionCart = computed<PromotionCart>(() => {
    return expandCartItemsToPromotionCart(this.inputs.cartItems());
  });

  private readonly evaluationContext = computed<EvaluationContext>(() => {
    const tz = this.inputs.timezone?.();
    return { currentDate: tz ? getDateReferenceForTimezone(tz) : new Date() };
  });

  private readonly qualifyingPromotions = computed<ApiV1_Promotion[]>(() => {
    const context = this.evaluationContext();
    return this.inputs.promotions().filter(
      p => p.isActive && evaluateConditions(p, context),
    );
  });

  readonly promotionResult = computed<PromotionResult>(() => {
    const cart = this.promotionCart();
    const promos = this.inputs.promotions();
    const context = this.evaluationContext();

    if (cart.items.length === 0 || promos.length === 0) {
      return {
        appliedPromotions: [],
        totalDiscount: 0,
        originalTotal: cart.subtotal,
        finalTotal: cart.subtotal,
      };
    }

    return applyToCart(promos, cart, context);
  });

  readonly totalDiscount = computed(() => this.promotionResult().totalDiscount);

  readonly appliedPromotions = computed(() => this.promotionResult().appliedPromotions);

  private readonly promotionsById = computed(() =>
    new Map(this.inputs.promotions().map(p => [p.id, p])),
  );

  readonly upsellHints = computed<UpsellHint[]>(() =>
    calculateUpsellHints(
      this.inputs.promotions(),
      this.promotionCart(),
      this.inputs.products(),
      this.evaluationContext(),
    ),
  );

  readonly participatingPromotionsByProductId = computed<Map<string, PromotionInput[]>>(() => {
    const cart = this.promotionCart();
    const applied = this.appliedPromotions();
    const promosById = this.promotionsById();
    const itemsById = new Map(cart.items.map(item => [item.id, item]));

    const result = new Map<string, PromotionInput[]>();
    const seenByProduct = new Map<string, Set<string>>();

    for (const appliedPromo of applied) {
      const promo = promosById.get(appliedPromo.promotionId);
      if (!promo) {
        continue;
      }

      const affectedProductIds = new Set<string>();
      for (const cartItemId of appliedPromo.affectedItemIds) {
        const item = itemsById.get(cartItemId);
        if (item) {
          affectedProductIds.add(item.productId);
        }
      }

      for (const productId of affectedProductIds) {
        let seen = seenByProduct.get(productId);
        if (!seen) {
          seen = new Set();
          seenByProduct.set(productId, seen);
        }
        if (seen.has(promo.id)) {
          continue;
        }
        seen.add(promo.id);

        const promoInput = appliedPromotionToInput(appliedPromo);
        const existing = result.get(productId);
        if (existing) {
          existing.push(promoInput);
        } else {
          result.set(productId, [promoInput]);
        }
      }
    }

    return result;
  });

  readonly availablePromotionsByProductId = computed<Map<string, PromotionInput[]>>(() => {
    const products = this.inputs.products();
    const promotions = this.qualifyingPromotions();
    const result = new Map<string, PromotionInput[]>();

    for (const product of products) {
      const matching = getPromotionsForProduct(promotions, product.id);
      if (matching.length > 0) {
        result.set(product.id, matching.map(toPromotionInput));
      }
    }

    return result;
  });

  /**
   * Total savings per product, aggregated across all its cart lines and
   * presentations. Used by the cart display to surface the FULL product
   * savings on every line of the same product (same amount on each), matching
   * the convention of the family tags (2x1, etc.) which are also repeated.
   */
  readonly savingsByProductId = computed<Map<string, number>>(() => {
    const cartItems = this.inputs.cartItems();
    const applied = this.appliedPromotions();
    const result = new Map<string, number>();

    for (const cartItem of cartItems) {
      const savings = getCartItemSavings(cartItem.id, applied);
      if (savings <= 0) {
        continue;
      }
      const productId = cartItem.product.id;
      result.set(productId, (result.get(productId) ?? 0) + savings);
    }

    return result;
  });

  private readonly rawUpsellHintsByCartItemId = computed<Map<string, UpsellHint[]>>(() => {
    const hints = this.upsellHints();
    const cartItems = this.inputs.cartItems();
    const result = new Map<string, UpsellHint[]>();

    const hintsByKey = new Map<string, UpsellHint[]>();
    for (const hint of hints) {
      const key = `${hint.productId}::${hint.presentationId ?? ''}`;
      let list = hintsByKey.get(key);
      if (!list) {
        list = [];
        hintsByKey.set(key, list);
      }
      list.push(hint);
    }

    for (const cartItem of cartItems) {
      const matched: UpsellHint[] = [];
      const presentationItems = cartItem.selectedPresentation?.items ?? [];
      if (presentationItems.length > 0) {
        for (const presentation of presentationItems) {
          if (!presentation.id || !presentation.quantity || presentation.quantity <= 0) {
            continue;
          }
          const key = `${cartItem.product.id}::${presentation.id}`;
          const list = hintsByKey.get(key);
          if (list && list.length > 0) {
            matched.push(...list);
          }
        }
      } else {
        const key = `${cartItem.product.id}::`;
        const list = hintsByKey.get(key);
        if (list && list.length > 0) {
          matched.push(...list);
        }
      }
      if (matched.length > 0) {
        result.set(cartItem.id, matched);
      }
    }

    return result;
  });

  /**
   * Upsell hints mapped per cart item, filtered by stock availability when a
   * `stock` signal is provided in the engine inputs. A hint is hidden when:
   *   - the target item isn't found in the stock map (catalog drift → orphan id), OR
   *   - the delta it suggests (`neededQuantity`) cannot be fulfilled given the stock
   *     available to add (= `availableStock - totalStockInCart`).
   * Lookup uses the hint's pre-resolved `itemId` (presentation.id when present,
   * product.id otherwise).
   */
  readonly upsellHintsByCartItemId = computed<Map<string, UpsellHint[]>>(() => {
    const rawHints = this.rawUpsellHintsByCartItemId();
    const stock = this.inputs.stock?.();
    if (!stock) {
      return rawHints;
    }

    const filtered = new Map<string, UpsellHint[]>();
    for (const [cartItemId, hints] of rawHints) {
      const visible = hints.filter(hint => {
        const info = stock.getStockInfoByItem(hint.itemId);
        if (info.hasUnlimitedStock) {
          return true;
        }
        const availableToAdd = info.availableStock - info.totalStockInCart;
        return hint.neededQuantity <= availableToAdd;
      });
      if (visible.length > 0) {
        filtered.set(cartItemId, visible);
      }
    }
    return filtered;
  });

  constructor(private inputs: CartPromotionsEngineInputs) {}
}
